import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import * as ACL from 'domain/restriction';

import Button from '@mui/material/Button';
import DocumentMoreButtonDropdown from 'pages/document/components/DocumentMoreButton/components/DocumentMoreButtonDropdown';
import { PaymentFooterPanel } from './StyledComponents';

import { filteredItem } from 'pages/dokkaPay/pages/payment/components/PaymentFooter/helpers/contextMenuHelper';
import { PAYMENT_STATUSES, PAYMENT_TAGS } from 'domain/payments/constants';
import {
  isApprovalRequiredSelector,
  paymentInfoSelector,
  paymentTagsSelector,
  shouldCurrentUserApproveSelector,
} from 'domain/payments';
import { PAY_IS_CONTROLLER, PAY_IS_OWNER } from 'domain/restriction';
import DialogRejectApproval from './components/DialogRejectApproval';
import usePaymentActions from './usePaymentActions';
import { canArchivePayment, canStartPaymentApprovalFlow, canStartPaymentProcess } from './helpers';

const mapStateToProps = (state: unknown) => ({
  isGranted: ACL.isGranted(state),
  restriction: ACL.myRestriction(state),
  paymentInfo: paymentInfoSelector(state),
  shouldCurrentUserApprove: shouldCurrentUserApproveSelector(state),
  paymentTags: paymentTagsSelector(state),
  isApprovalRequired: isApprovalRequiredSelector(state),
});

const PaymentFooter = () => {
  const { formatMessage } = useIntl();

  const [openRejectModal, setOpenRejectModal] = useState(false);

  const { restriction, paymentInfo, isGranted, shouldCurrentUserApprove, paymentTags, isApprovalRequired } =
    useSelector(mapStateToProps);

  const handleOpenRejectDialog = useCallback(() => {
    setOpenRejectModal(true);
  }, []);

  const handleCloseRejectDialog = useCallback(() => {
    setOpenRejectModal(false);
  }, []);

  const {
    handleStartApprovalFlow,
    handleResetProcess,
    isVisibleResetProcessBtn,
    getActionButtons,
    handleArchive,
    handleUnarchive,
    handleSaveForLater,
    handleStartPaymentProcess,
  } = usePaymentActions({
    isGranted,
    handleOpenRejectDialog,
    paymentInfo,
    shouldCurrentUserApprove,
    paymentTags,
    isApprovalRequired,
  });

  const contextMenuItems = useMemo(() => {
    const items = {
      startPaymentProcess: {
        action: handleStartPaymentProcess,
        disabled: !canStartPaymentProcess(paymentTags),
      },
      sendToApproval: {
        action: handleStartApprovalFlow,
        disabled: !canStartPaymentApprovalFlow(paymentInfo),
      },
      sendByEmail: {
        action: () => {
          console.log('SEND BY EMAIL ACTION');
        },
        // Approver-only users shouldn't see the 'sendToApproval' menu, so we hide the divider
        divided: isGranted(PAY_IS_OWNER) || isGranted(PAY_IS_CONTROLLER),
        disabled: true,
      },
      printDocument: {
        action: () => {
          console.log('PRINT ACTION');
        },
        disabled: true,
      },
      downloadDocument: {
        action: () => {
          console.log('DOWNLOAD ACTION');
        },
        disabled: true,
      },
      saveForLater: {
        action: handleSaveForLater,
        disabled: paymentInfo.status !== PAYMENT_STATUSES.PREPARATION,
        isHidden: paymentTags.includes(PAYMENT_TAGS.saveForLater),
        divided: true,
      },
      archive: {
        action: handleArchive,
        disabled: !canArchivePayment(paymentInfo.status),
        isHidden: paymentTags.includes(PAYMENT_TAGS.archive),
      },
      unarchive: {
        action: handleUnarchive,
        isHidden: !paymentTags.includes(PAYMENT_TAGS.archive),
      },
    };

    return filteredItem({ restriction }).map((o) => {
      const item = (items as Record<string, any>)[o.id] ?? {};
      return {
        ...o,
        ...item,
        label: { id: `contextMenu.${o.id}`, defaultMessage: o.title },
        onClick: item.action,
      };
    });
  }, [
    handleStartPaymentProcess,
    paymentTags,
    handleStartApprovalFlow,
    paymentInfo,
    isGranted,
    handleSaveForLater,
    handleArchive,
    handleUnarchive,
    restriction,
  ]);

  return (
    <PaymentFooterPanel gap={2}>
      <DocumentMoreButtonDropdown options={contextMenuItems} />
      {isVisibleResetProcessBtn && (
        <Button variant="text" color="error" onClick={handleResetProcess}>
          {formatMessage({ id: 'payment.footer.actionButton.resetProcess', defaultMessage: 'Reset the process' })}
        </Button>
      )}
      {getActionButtons()}
      {openRejectModal && <DialogRejectApproval onClose={handleCloseRejectDialog} id={paymentInfo.id} />}
    </PaymentFooterPanel>
  );
};

export default PaymentFooter;
